import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";

//... Import Azure services
import { baseUrl } from "../../services/AzureStorage";

//... Import common entities
import Helper from "../../common/Helper";
import RouteConstants from "../../common/RouteConstants";

//... Imports context provider.
import { AppDataContext } from "../../common/appContext";

//... Imports components.
import IndividualCombineStatsBarChart from "./IndividualCombineStatsBarChart";

//... Imports Shared components.
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "../../sharedComponents/Accordion/Accordion";
import TitleTooltip from "../../sharedComponents/Tooltip/TitleTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "5px 15px",
    border: "1px solid lightgrey",
    borderRadius: "10px",
    boxShadow: "2px 2px 11px grey inset",
  },
  listItemIcon: {
    minWidth: "38px",
  },
  imageIcon: {
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  iconRoot: {
    textAlign: "center",
    width: "100%",
    height: "50px",
  },
  cardStyle: {
    border: "1px solid lightgrey",
    borderRadius: "10px",
    height: "250px",
  },

  // headerText: {
  //   marginBottom: theme.spacing(0.5),
  //   color: "rgb(29, 66, 138)",
  // },
  headerText2: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerTextInfo: {
    color: "rgb(29, 66, 138)",
    fontSize: "14px",
  },
  headerBox: {
    background: "#c1d0ed",
  },
  smBlue: {
    marginLeft: "16px",
    width: 26,
    height: 26,
    color: theme.palette.getContrastText("#0c2c75"),
    backgroundColor: "#1e3f7e",
    position: "relative",
    overflow: "hidden",
    borderRadius: "50%",
  },
  playerNameStyle: {
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
  dataField: {
    backgroundColor: "#1d428a",
    color: "white",
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "400",
    textAlign: "center",
  },
  percentileField: {
    width: "fit-content",
    padding: "3px",
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
  cellContent: {
    padding: "5px",
    textAlign: "center",
  },
  percentileCellContent: {
    display: "flex",
    justifyContent: "center",
  },
  customDivider: {
    marginTop: "8px",
    marginBottom: "8px",
    "&.MuiDivider-root": {
      "&::before": {
        borderTop: "4px solid #1d428a",
      },
      "&::after": {
        borderTop: "4px solid #1d428a",
      },
    },
    "& .MuiDivider-wrapper": {
      fontSize: 16,
    },
  },
}));

const defaultColorSchema = "rgb(186 28 28 / 87%)";

const IndividualPercentileAnthros = ({
  anthros,
  loading,
  eventId,
  validateNumber,
  anthroPercentileStats,
  anthroAVGStats,
  colorSchemaForGroups,
  expandedGroups,
  handleGroupAccordionChange,
}) => {
  const classes = useStyles();
  let navigate = useNavigate();

  const {
    getWeightUnitValue,
    getLengthUnitValue,
    getHandDimensionsUnitValue,
    getHandDimensionsUnitLabelType,
    lengthUnits,
    getWeightUnitLabelType,
    weightUnits,
    getLengthUnitLabelType,
  } = useContext(AppDataContext);

  const [graphDataSet, setGraphDataSet] = useState({});
  const [avgGraphDataSet, setAvgGraphDataSet] = useState({});

  const [chartType, setChartType] = useState("avg");

  const handleChangeChartType = (event) => {
    setChartType(event.target.value);
  };

  useEffect(() => {
    if (anthroPercentileStats?.length > 0) {
      setGraphDataSet(preapreGraphDataSet(anthroPercentileStats));
    }
  }, [anthroPercentileStats]);

  useEffect(() => {
    if (anthroAVGStats?.length > 0) {
      setAvgGraphDataSet(preapreGraphDataSet(anthroAVGStats));
    }
  }, [anthroAVGStats]);

  const preapreGraphDataSet = (inputData) => {
    if (!inputData || inputData.length === 0)
      return { labels: [], datasets: [] };

    const groups = Object.keys(inputData[0]).filter((key) =>
      key.includes("group")
    );

    const datasets = groups.map((group, index) => ({
      label: `Group - ${index + 1}\n\u00A0`,
      data: formatData(inputData, group),
      backgroundColor: colorSchemaForGroups[index] || defaultColorSchema,
      borderColor: colorSchemaForGroups[index] || defaultColorSchema,
      borderWidth: 1,
      fill: false,
    }));

    return { labels: inputData.map((item) => item.category), datasets };
  };

  function formatData(inputData, group) {
    return inputData.map((item) => {
      const floatValue = parseFloat(item[group]);
      return Number.isNaN(floatValue) ||
        floatValue < 0 ||
        floatValue > 1 ||
        floatValue === 0
        ? floatValue
        : floatValue.toFixed(2);
    });
  }

  const viewPlayerProfile = (playerId) => {
    navigate(`/${eventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`, {
      state: { from: "CombineResults", tab: 0 },
    });
  };

  const historicalPercentileAnthrosColumn = [
    {
      id: "Player Name",
      headerName: "Player Name",
      style: { padding: "4px", paddingLeft: "25px", width: "200px" },
      unit: "",
    },
    {
      id: "Position",
      headerName: "Position",
      style: { padding: "4px", textAlign: "center", width: "100px" },
      unit: "",
    },
    {
      id: "College Name",
      headerName: "College Name",
      style: { padding: "4px", textAlign: "center", width: "250px" },
      unit: "",
    },
    {
      id: "Standing Reach",
      headerName: "Standing Reach",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `${getLengthUnitLabelType(lengthUnits)}`,
    },
    {
      id: "WingSpan Feet",
      headerName: "WingSpan Feet",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `${getLengthUnitLabelType(lengthUnits)}`,
    },
    {
      id: "Height WithOut Shoes",
      headerName: "Height W/o Shoes",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `${getLengthUnitLabelType(lengthUnits)}`,
    },
    // {
    //   id: "Body Weight",
    //   headerName: "Body Weight",
    //   style: {
    //     padding: "4px",
    //     textAlign: "center",
    //     verticalAlign: "top",
    //     width: "200px",
    //   },
    //   unit: `${getWeightUnitLabelType(weightUnits)}`,
    // },
    {
      id: "Hand Length",
      headerName: "Hand Length",
      style: {
        padding: "4px",
        textAlign: "center",
        verticalAlign: "top",
        width: "200px",
      },
      unit: `W${getHandDimensionsUnitLabelType(
        lengthUnits
      )} | L${getHandDimensionsUnitLabelType(lengthUnits)}`,
    },
  ];

  const RenderChartFilterSection = () => {
    return (
      <Box display="flex" justifyContent="center">
        <FormControl component="fieldset">
          <RadioGroup row value={chartType} onChange={handleChangeChartType}>
            <FormControlLabel value="avg" control={<Radio />} label="AVG" />
            <FormControlLabel
              value="percentile"
              control={<Radio />}
              label="Percentile"
            />
          </RadioGroup>
        </FormControl>
      </Box>
    );
  };

  const RenderAvgTableRow = ({ avg }) => {
    return (
      avg &&
      Object.keys(avg).length > 0 && (
        <TableRow
          sx={{
            backgroundColor: "#F5F5F5",
          }}
        >
          <TableCell
            className={classes.stickyColumn}
            sx={{ padding: "4px" }}
          ></TableCell>
          <TableCell className={classes.cellContent}></TableCell>
          <TableCell className={classes.cellContent}>
            <Typography variant="subtitle2" display="block" gutterBottom>
              {"AVG"}
            </Typography>
          </TableCell>
          <TableCell className={classes.cellContent}>
            {getLengthUnitValue(
              validateNumber(avg.standingReachFeet),
              validateNumber(avg.standingReachInches)
            )}
          </TableCell>
          <TableCell className={classes.cellContent}>
            {getLengthUnitValue(
              validateNumber(avg.wingSpanFeet),
              validateNumber(avg.wingSpanInches)
            )}
          </TableCell>
          <TableCell className={classes.cellContent}>
            {getLengthUnitValue(
              validateNumber(avg.heightWithOutShoesFeet),
              validateNumber(avg.heightWithOutShoesInches)
            )}
          </TableCell>
          {/* <TableCell className={classes.cellContent}>
            {getWeightUnitValue(validateNumber(avg.bodyWeight))}
          </TableCell> */}
          <TableCell className={classes.cellContent}>
            {getHandDimensionsUnitValue(validateNumber(avg.handWidth))} |{" "}
            {getHandDimensionsUnitValue(validateNumber(avg.handLength))}
          </TableCell>
        </TableRow>
      )
    );
  };

  const RenderPercentileTableRow = ({ percentile }) => {
    return (
      false &&
      percentile &&
      Object.keys(percentile).length > 0 && (
        <TableRow
          sx={{
            backgroundColor: "#F5F5F5",
          }}
        >
          <TableCell
            className={classes.stickyColumn}
            sx={{ padding: "4px" }}
          ></TableCell>
          <TableCell className={classes.cellContent}>{""}</TableCell>
          <TableCell className={classes.cellContent}>
            <Typography variant="subtitle2" display="block" gutterBottom>
              {"PERCENTILE"}
            </Typography>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.standingReachInches
                  ? `${percentile.standingReachInches}%`
                  : `0%`}
              </Box>
            </Box>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.wingSpanInches
                  ? `${percentile.wingSpanInches}%`
                  : `0%`}
              </Box>
            </Box>
          </TableCell>
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.heightWithOutShoesInches
                  ? `${percentile.heightWithOutShoesInches}%`
                  : `0%`}
              </Box>
            </Box>
          </TableCell>
          {/* <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.bodyWeight ? `${percentile.bodyWeight}%` : `0%`}
              </Box>
            </Box>
          </TableCell> */}
          <TableCell className={classes.cellContent}>
            <Box className={classes.percentileCellContent}>
              <Box className={classes.dataField}>
                {percentile.handWidth ? `${percentile.handWidth}%` : `0%`}|{" "}
                {percentile.handLength ? `${percentile.handLength}%` : `0%`}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )
    );
  };

  return (
    <>
      <Container maxWidth={false}>
        <Box p={1}>
          <Box sx={{ marginTop: "1.5rem" }}>
            {anthros &&
              anthros.map((groups, index) => {
                const { groupID, data, avg, percentile } = groups;
                return (
                  <React.Fragment key={index}>
                    <Accordion
                      expanded={expandedGroups?.anthros?.[index]?.expanded}
                      onChange={() =>
                        handleGroupAccordionChange("anthros", index)
                      }
                      borderColor={`${colorSchemaForGroups[index]}`}
                    >
                      <AccordionSummary
                        aria-controls="panel2d-content"
                        id="panel2d-header"
                        className="accordion-sub-header"
                        color={`${colorSchemaForGroups[index]}`}
                        expanded={expandedGroups?.anthros?.[index]?.expanded}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box className={classes.headerBox}>
                            <Typography
                              className={`${classes.headerText} accordionTitle`}
                              style={{
                                color: `${colorSchemaForGroups[index]}`,
                              }}
                            >
                              {`Group - ${groupID}`.toUpperCase()}
                            </Typography>
                          </Box>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box p={2}>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  {historicalPercentileAnthrosColumn.map(
                                    (col, index) => (
                                      <TitleTooltip
                                        title={col.id}
                                        placement="top-start"
                                        key={index}
                                      >
                                        <TableCell style={col.style}>
                                          {col.headerName}
                                        </TableCell>
                                      </TitleTooltip>
                                    )
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableHead>
                                <TableRow>
                                  {historicalPercentileAnthrosColumn.map(
                                    (col, index) => (
                                      <TableCell style={col.style} key={index}>
                                        {col.unit}
                                      </TableCell>
                                    )
                                  )}
                                </TableRow>
                              </TableHead>
                              {data && data.length > 0 ? (
                                <>
                                  <TableBody>
                                    {/* Display Comparable Group Datasets for Anthropometry */}
                                    {data?.map((val, index) => {
                                      return (
                                        <TableRow key={index}>
                                          <TableCell
                                            className={classes.stickyColumn}
                                            sx={{ padding: "4px" }}
                                          >
                                            <Box display="flex">
                                              {val.headshotURL &&
                                              val.headshotURL !== "" ? (
                                                <Avatar
                                                  className={classes.smBlue}
                                                  sx={{
                                                    bgcolor: "#1d428a",
                                                    cursor: "pointer",
                                                  }}
                                                  src={
                                                    val.headshotURL
                                                      ? baseUrl(val.headshotURL)
                                                      : ""
                                                  }
                                                  alt="basketball"
                                                  onClick={() =>
                                                    viewPlayerProfile(
                                                      val.playerID
                                                    )
                                                  }
                                                >
                                                  <Typography variant="caption">
                                                    {Helper.GetNameInitials(
                                                      `${val.playerName}`
                                                    )}
                                                  </Typography>
                                                </Avatar>
                                              ) : (
                                                <Avatar
                                                  className={classes.smBlue}
                                                  sx={{
                                                    bgcolor: "#1d428a",
                                                    cursor: "pointer",
                                                  }}
                                                  alt="basketball"
                                                  onClick={() =>
                                                    viewPlayerProfile(
                                                      val.playerID
                                                    )
                                                  }
                                                >
                                                  <Typography variant="caption">
                                                    {Helper.GetNameInitials(
                                                      `${val.playerName}`
                                                    )}
                                                  </Typography>
                                                </Avatar>
                                              )}
                                              &nbsp;&nbsp;
                                              <TitleTooltip
                                                title="View Player Profile"
                                                placement="right-start"
                                              >
                                                <Typography
                                                  variant="body2"
                                                  className={
                                                    classes.playerNameStyle
                                                  }
                                                  onClick={() =>
                                                    viewPlayerProfile(
                                                      val.playerID
                                                    )
                                                  }
                                                >
                                                  {val.playerName}
                                                </Typography>
                                              </TitleTooltip>
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.position ? val.position : "-"}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {val.collegeOrClub
                                              ? val.collegeOrClub
                                              : "-"}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {getLengthUnitValue(
                                              validateNumber(
                                                val.standingReachFeet
                                              ),
                                              validateNumber(
                                                val.standingReachInches
                                              )
                                            )}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {getLengthUnitValue(
                                              validateNumber(val.wingSpanFeet),
                                              validateNumber(val.wingSpanInches)
                                            )}
                                          </TableCell>
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {getLengthUnitValue(
                                              validateNumber(
                                                val.heightWithOutShoesFeet
                                              ),
                                              validateNumber(
                                                val.heightWithOutShoesInches
                                              )
                                            )}
                                          </TableCell>
                                          {/* <TableCell
                                                className={classes.cellContent}
                                              >
                                                {getWeightUnitValue(
                                                  validateNumber(val.bodyWeight)
                                                )}
                                              </TableCell> */}
                                          <TableCell
                                            className={classes.cellContent}
                                          >
                                            {getHandDimensionsUnitValue(
                                              validateNumber(val.handWidth)
                                            )}
                                            |{" "}
                                            {getHandDimensionsUnitValue(
                                              validateNumber(val.handLength)
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}

                                    {/* Display Comparable Group Average Details for Anthropometry */}
                                    <RenderAvgTableRow avg={avg} />

                                    {/* Display Comparable Group Percentile Details for Anthropometry */}
                                    <RenderPercentileTableRow
                                      percentile={percentile}
                                    />
                                  </TableBody>
                                </>
                              ) : (
                                <>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell colSpan={10} align="center">
                                        No Records Found
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </>
                              )}
                            </Table>
                          </TableContainer>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* Conditionally render the Divider if the current item is not the last item */}
                    {index !== anthros.length - 1 && (
                      <Divider className={classes.customDivider} sx={{ my: 2 }}>
                        VS
                      </Divider>
                    )}
                  </React.Fragment>
                );
              })}
          </Box>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ my: 2 }}
          >
            <Grid item md={7} sm={12} xs={12} justifyContent="center">
              <RenderChartFilterSection />

              {Object.keys(graphDataSet).length > 0 &&
                chartType === "percentile" && (
                  <IndividualCombineStatsBarChart
                    chartName={"Percentile Chart"}
                    data={graphDataSet}
                    max={120}
                  />
                )}

              {Object.keys(avgGraphDataSet).length > 0 &&
                chartType === "avg" && (
                  <IndividualCombineStatsBarChart
                    chartName={"AVG Chart"}
                    data={avgGraphDataSet}
                    max={500}
                    chartType={"AVG"}
                  />
                )}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default IndividualPercentileAnthros;
